import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSnapshot } from "valtio";
import { appStore } from "@store/appStore";
import MarketingBanner from "@/shared/components/MarketingBanner/MarketingBanner";
import { MARKETING_BANNERS, useFeatureFlag } from "@/shared/featureFlags/useFeatureFlag";
import { readPreview } from "./bannerPreview";
import { useCurrentBanner } from "./useCurrentBanner";
import BannerControls from "./BannerControls";
import { bannerPreviewStore } from "./bannerPreviewStore";
import "./dashboardBanner.scss";

interface DashboardBannerProps {
    /**
     * Honor a banner preview arriving in router state from the banner editor. True on the
     * Partner Dashboard, the only page the editor previews on; false on the Legacy Dashboard
     * (MPN-2320), which shows the live banner only and leaves router state alone.
     */
    preview?: boolean;
}

/**
 * The marketing banner across the top of the Partner Dashboard (MPN-2266) and the Legacy
 * Dashboard (MPN-2320), behind marketing-banners. One of: nothing; the live banner, with the
 * controls strip for Banner Managers; or, where previews are honored, an admin's unsaved
 * preview from the banner editor.
 */
export default function DashboardBanner({ preview: previewAllowed = true }: DashboardBannerProps = {}) {
    const flagOn = useFeatureFlag(MARKETING_BANNERS);
    const canManage = useSnapshot(appStore).loggedInUser?.permissions.hasSomeAccess("Manage Banners") ?? false;
    const location = useLocation();
    const navigate = useNavigate();

    // The flags may arrive after the first render; the state captured on mount keeps the preview.
    const arrivedWith = useRef(location.state);
    const [ended, setEnded] = useState(false);
    const preview = useMemo(
        () => (previewAllowed && flagOn && canManage && !ended ? readPreview(arrivedWith.current) : null),
        [previewAllowed, flagOn, canManage, ended],
    );

    // Drop the preview from history, so a reload or Back/Forward lands on the plain dashboard.
    const replacing = useRef(false);
    useEffect(() => {
        if (previewAllowed && flagOn && readPreview(location.state)) {
            replacing.current = true;
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
    }, [flagOn]);

    // Any navigation after that replace (e.g. Partner Dashboard in the sidebar, which keeps this
    // component mounted) is a fresh visit: end the preview and show the live banner.
    const arrivalKey = useRef(location.key);
    const ownKey = useRef<string | null>(null);
    useEffect(() => {
        if (location.key === arrivalKey.current) return;
        if (replacing.current) {
            replacing.current = false;
            ownKey.current = location.key;
        } else if (ownKey.current !== null && location.key !== ownKey.current) {
            setEnded(true);
        }
    }, [location.key]);

    // The app shell draws the ADMIN PREVIEW bar from the store, above the sidebar and topbar.
    useEffect(() => {
        if (!preview) return;
        bannerPreviewStore.preview = preview;
        return () => {
            bannerPreviewStore.preview = null;
        };
    }, [preview]);

    // <main> keeps the editor's scroll position, which would leave the preview out of view.
    const previewTop = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (preview) previewTop.current?.scrollIntoView?.({ block: "start" });
    }, [preview]);

    const { banner, hide } = useCurrentBanner(flagOn && !preview);

    if (!flagOn) return null;

    if (preview) {
        const { bannerPreview } = preview;
        return (
            <div ref={previewTop}>
                <MarketingBanner message={bannerPreview.message} linkText={bannerPreview.link_text} linkUrl={bannerPreview.link_url} />
            </div>
        );
    }

    if (!banner) return null;

    return (
        <>
            <MarketingBanner message={banner.message} linkText={banner.link_text} linkUrl={banner.link_url} />
            {canManage && <BannerControls banner={banner} onTurnedOff={hide} />}
        </>
    );
}
